// Firebase SDK imports
import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";

// ===================================
// Firebase Configuration
// ===================================
const firebaseConfig = {
  apiKey: "AIzaSyA6CXLaYLt4wMLSvsCInrjvDDchCK34eq4",
  authDomain: "barren-blue.firebaseapp.com",
  projectId: "barren-blue",
  storageBucket: "barren-blue.firebasestorage.app",
  messagingSenderId: "1066941599433",
  appId: "1:1066941599433:web:ccbb81ceab1a77317e4718",
  measurementId: "G-D125HSK7PT"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

// ===================================
// Analytics Utilities
// ===================================
class Analytics {
  /**
   * Track page view with UTM parameters
   */
  static trackPageView() {
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('utm_source') || 'direct';
    const medium = urlParams.get('utm_medium') || 'none';
    const campaign = urlParams.get('utm_campaign') || 'none';

    logEvent(analytics, 'page_view', {
      campaign_source: source,
      campaign_medium: medium,
      campaign_name: campaign,
      page_location: window.location.href,
      page_title: document.title
    });
  }

  /**
   * Track outbound link clicks
   * @param {string} category - Link category (e.g., 'streaming', 'social')
   * @param {string} platform - Platform name (e.g., 'spotify', 'instagram')
   */
  static trackLinkClick(category, platform) {
    logEvent(analytics, 'link_click', {
      link_category: category,
      platform: platform
    });
  }

  /**
   * Track form submissions
   * @param {string} formName - Name of the form
   * @param {boolean} success - Whether submission was successful
   */
  static trackFormSubmission(formName, success) {
    logEvent(analytics, 'form_submission', {
      form_name: formName,
      success: success
    });
  }
}

// ===================================
// UI Utilities
// ===================================
class UI {
  /**
   * Set up intersection observer for fade-in animations
   */
  static setupScrollAnimations() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observe all sections
    document.querySelectorAll('section').forEach((section) => {
      section.style.opacity = '0';
      section.style.transform = 'translateY(20px)';
      section.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
      observer.observe(section);
    });
  }

  /**
   * Add quote indices for staggered animations
   */
  static setupQuoteAnimations() {
    document.querySelectorAll('.press-quote').forEach((quote, index) => {
      quote.style.setProperty('--quote-index', index);
    });
  }

  /**
   * Show message in form feedback area
   * @param {HTMLElement} messageElement - The message container element
   * @param {string} message - The message text
   * @param {string} type - Message type ('success' or 'error')
   */
  static showFormMessage(messageElement, message, type) {
    messageElement.textContent = message;
    messageElement.className = `form-message ${type}`;

    // Clear message after 5 seconds
    setTimeout(() => {
      messageElement.textContent = '';
      messageElement.className = 'form-message';
    }, 5000);
  }
}

// ===================================
// Event Handlers
// ===================================
class EventHandlers {
  /**
   * Set up tracking for streaming platform links
   */
  static setupStreamingTracking() {
    const platforms = ['bandcamp', 'spotify', 'youtube', 'apple-music', 'amazon-music'];

    document.querySelectorAll('.platform-links .platform-button').forEach(link => {
      link.addEventListener('click', () => {
        const platform = Array.from(link.classList)
          .find(cls => platforms.includes(cls));

        if (platform) {
          Analytics.trackLinkClick('streaming', platform);
        }
      });
    });
  }

  /**
   * Set up tracking for social media links
   */
  static setupSocialTracking() {
    const platforms = ['spotify', 'bandcamp', 'tiktok', 'instagram', 'soundcloud'];

    document.querySelectorAll('.social-links .social-button').forEach(link => {
      link.addEventListener('click', () => {
        const platform = Array.from(link.classList)
          .find(cls => platforms.includes(cls));

        if (platform) {
          Analytics.trackLinkClick('social', platform);
        }
      });
    });
  }

  /**
   * Set up Buttondown mailing list form submission
   */
  static setupMailingListForm() {
    const form = document.getElementById('mailingListForm');
    const messageElement = document.getElementById('formMessage');

    if (!form || !messageElement) return;

    // Buttondown username
    const BUTTONDOWN_USERNAME = 'barrenblue';

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const emailInput = form.querySelector('input[type="email"]');
      const email = emailInput.value.trim();

      if (!email) {
        UI.showFormMessage(messageElement, 'Please enter a valid email address.', 'error');
        return;
      }

      // Disable form during submission
      const submitButton = form.querySelector('button[type="submit"]');
      const originalText = submitButton.textContent;
      submitButton.disabled = true;
      submitButton.textContent = 'Signing up...';

      try {
        // Submit to Buttondown public embed endpoint (no API key needed!)
        // The embed endpoint expects form data, not JSON
        const formData = new FormData();
        formData.append('email', email);

        const response = await fetch(`https://buttondown.com/api/emails/embed-subscribe/${BUTTONDOWN_USERNAME}`, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          UI.showFormMessage(
            messageElement,
            'Thanks for signing up! Check your email to confirm.',
            'success'
          );
          Analytics.trackFormSubmission('mailing_list', true);
          emailInput.value = '';
        } else {
          // Buttondown returns 200 for duplicates, but let's handle errors
          const errorText = await response.text();

          if (errorText.includes('already subscribed') || errorText.includes('already exists')) {
            UI.showFormMessage(
              messageElement,
              'This email is already subscribed!',
              'error'
            );
          } else {
            UI.showFormMessage(
              messageElement,
              'Something went wrong. Please try again.',
              'error'
            );
          }

          Analytics.trackFormSubmission('mailing_list', false);
        }
      } catch (error) {
        console.error('Form submission error:', error);
        UI.showFormMessage(
          messageElement,
          'Network error. Please check your connection and try again.',
          'error'
        );
        Analytics.trackFormSubmission('mailing_list', false);
      } finally {
        // Re-enable form
        submitButton.disabled = false;
        submitButton.textContent = originalText;
      }
    });
  }
}

// ===================================
// Application Initialization
// ===================================
class App {
  /**
   * Initialize the application
   */
  static init() {
    // Track page view
    Analytics.trackPageView();

    // Set up UI animations
    UI.setupScrollAnimations();
    UI.setupQuoteAnimations();

    // Set up event handlers
    EventHandlers.setupStreamingTracking();
    EventHandlers.setupSocialTracking();
    EventHandlers.setupMailingListForm();

    console.log('Barren Blue website initialized');
  }
}

// ===================================
// DOM Ready
// ===================================
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => App.init());
} else {
  // DOM is already ready
  App.init();
}

// Export for potential testing or extension
export { Analytics, UI, EventHandlers, App };
